import React, { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from 'react-router';
import { format, parseISO } from 'date-fns';
import CheckSVG from '@birdi/icons/check.svg';
import MapSVG from '@birdi/icons/map.svg';
import ArrowRightSVG from '@birdi/icons/arrow-right.svg';
import { BirdiTheme, buttonBlue, buttonOutlineBlue, forms } from '@birdi/theme';
import { maps as mapsAPI, stroot } from '@birdi/js-sdk';
import { mapSitesAPI } from '@birdi/js-sdk/src/public/map-sites';
import type {
  MapSiteReferenceSet,
  MapSiteReferenceSetKind,
} from '@birdi/js-sdk/src/public/map-sites';
import { invalidateSites, useReferenceSets, useSiteDemLayers } from './queries';
import { ModalShell } from './site-modals';
import { modalFooterCSS, modalHalfButtonCSS } from './styles';
import { readAPIErrorMessage } from './utils';

const REFERENCE_SET_LABEL: Record<MapSiteReferenceSetKind, string> = {
  gcp: 'GCP set',
  prism: 'Prism network set',
};

// Title with the map icon, per the design.
const titleRowCSS = css`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  svg {
    flex-shrink: 0;
    width: 1.1em;
    height: 1.1em;
  }
`;

// Green-outlined confirmation strip: the new map is auto-added to this site.
const autoAddedChipCSS = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.8rem;
  margin-bottom: 1.25rem;
  border: 1px solid ${theme.flora};
  border-radius: 6px;
  background: rgba(74, 210, 148, 0.08);
  color: ${theme.midnight};
  font-size: 0.9rem;
  svg {
    flex-shrink: 0;
    width: 1em;
    height: 1em;
    color: ${theme.flora};
  }
`;

const sectionHeadingCSS = (theme: BirdiTheme) => css`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${theme.midnight};
`;

const sectionSubtextCSS = (theme: BirdiTheme) => css`
  margin: 0.15rem 0 0.75rem;
  font-size: 0.85rem;
  color: ${theme.mono50};
`;

// Filled checklist panel. min-height keeps its shape when there's little or
// nothing to carry across; max-height + overflow means ONLY this panel scrolls
// when the list is long — the modal itself never grows or scrolls.
const listBoxCSS = (theme: BirdiTheme) => css`
  background: ${theme.sand};
  border-radius: 6px;
  padding: 0.35rem;
  min-height: 9rem;
  max-height: 14rem;
  overflow-y: auto;
`;

const checkRowCSS = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.3rem 0.45rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  line-height: 1.3;
  color: ${theme.midnight};
  /* A touch darker than the panel, so hover reads without boxing each row. */
  &:hover {
    background: ${theme.dove};
  }
  input {
    flex-shrink: 0;
    accent-color: ${theme.deepOcean};
    width: 1rem;
    height: 1rem;
  }
`;

const rowSubtextCSS = (theme: BirdiTheme) => css`
  color: ${theme.mono50};
  font-size: 0.8rem;
`;

const emptyHintCSS = (theme: BirdiTheme) => css`
  padding: 0.45rem;
  font-size: 0.85rem;
  color: ${theme.mono50};
`;

// Full-width footer: the two buttons split the whole modal width evenly.
const cancelFullCSS = (theme: BirdiTheme) => css`
  ${buttonOutlineBlue(theme)};
  ${modalHalfButtonCSS};
`;

const createFullCSS = (theme: BirdiTheme) => css`
  ${buttonBlue(theme)};
  ${modalHalfButtonCSS};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  svg {
    width: 1em;
    height: 1em;
  }
`;

/** The current (highest) version of a reference set, mirroring ReferenceSetsTab. */
const currentVersionOf = (set: MapSiteReferenceSet) =>
  set.versions.find((v) => v.version === set.currentVersion) ??
  set.versions[0] ??
  null;

const fmtDate = (iso: string | null | undefined) =>
  iso ? format(parseISO(iso), 'd MMM yyyy') : 'Unknown';

interface NewMapModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  site: { id: string; name: string };
}

/**
 * "New map" from the Site page: creates a map, adds it to this site, and brings
 * across the reference sets / prior DEM layers the user ticked — the API
 * materialises both as real layers on the new map — then opens the new map.
 * The uploader is NOT opened: the map may already have data carried across, so
 * the user decides whether to upload.
 *
 * No name field — the map is created as "Untitled" (the API default) and can be
 * renamed on the map itself, matching the design.
 */
export const NewMapModal = ({ open, setOpen, site }: NewMapModalProps) => {
  const navigate = useNavigate();
  const [selectedRefIds, setSelectedRefIds] = useState<Set<string>>(new Set());
  const [selectedDemIds, setSelectedDemIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: referenceSets = [] } = useReferenceSets(site.id);
  const { data: demLayers = [] } = useSiteDemLayers(site.id, open);

  // Reference-set rows that actually have an uploaded version to carry.
  const refRows = useMemo(
    () =>
      referenceSets
        .map((set) => ({ set, current: currentVersionOf(set) }))
        .filter((r) => r.current !== null),
    [referenceSets],
  );

  useEffect(() => {
    if (open) {
      setSelectedRefIds(new Set());
      setSelectedDemIds(new Set());
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const toggle = (
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    id: string,
  ) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const hasBringAcross = refRows.length > 0 || demLayers.length > 0;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // No name — the API defaults to "Untitled".
      const res = await mapsAPI.postMap(stroot('nmcre8'), { flow: 'upload' });
      const mapId = res.body?.id;
      if (!mapId) throw new Error('Map could not be created');
      // Assign to this site + carry the selection across in one call.
      // The new map has no location yet, so auto-grouping wouldn't pick it up.
      await mapSitesAPI.addMapToSite(stroot('nmasgn'), site.id, mapId, {
        referenceSetVersionIds: [...selectedRefIds],
        demLayerIds: [...selectedDemIds],
      });
      invalidateSites(site.id);
      setOpen(false);
      navigate(`/map/next/${mapId}`);
    } catch (err) {
      setError(readAPIErrorMessage(err));
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      setOpen={setOpen}
      title={
        <span css={titleRowCSS}>
          <MapSVG />
          New map - {site.name}
        </span>
      }
    >
      <div css={autoAddedChipCSS} data-testid="new-map-added-chip">
        <CheckSVG />
        <span>Auto-added to {site.name} based on the data / map location</span>
      </div>

      <div>
        <h4 css={sectionHeadingCSS}>
          Bring across data from previous maps at this location
        </h4>
        <p css={sectionSubtextCSS}>
          Optional — tick anything you want carried into the new map
        </p>

        <div css={listBoxCSS} data-testid="new-map-bring-across">
          {!hasBringAcross && (
            <div css={emptyHintCSS}>
              No reference sets or DEMs at this location yet.
            </div>
          )}

          {refRows.map(({ set, current }) => (
            <label key={current!.id} css={checkRowCSS}>
              <input
                type="checkbox"
                data-testid={`new-map-ref-${set.kind}`}
                checked={selectedRefIds.has(current!.id)}
                onChange={() => toggle(setSelectedRefIds, current!.id)}
              />
              <span>
                {REFERENCE_SET_LABEL[set.kind as MapSiteReferenceSetKind] ??
                  'Reference set'}{' '}
                — {fmtDate(current!.createdAt)}
              </span>
            </label>
          ))}

          {demLayers.map((layer) => (
            <label key={layer.layerId} css={checkRowCSS}>
              <input
                type="checkbox"
                data-testid="new-map-dem"
                checked={selectedDemIds.has(layer.layerId)}
                onChange={() => toggle(setSelectedDemIds, layer.layerId)}
              />
              <span>
                {layer.name || 'DEM'} — {fmtDate(layer.capturedAt)}
                <br />
                <span css={rowSubtextCSS}>{layer.mapName}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {error && <div css={forms.errorMessage}>{error}</div>}

      <footer css={modalFooterCSS}>
        <button
          type="button"
          css={cancelFullCSS}
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          css={createFullCSS}
          data-testid="new-map-create-button"
          disabled={submitting}
          onClick={submit}
        >
          {submitting ? 'Creating…' : 'Create map'}
          {!submitting && <ArrowRightSVG />}
        </button>
      </footer>
    </ModalShell>
  );
};
